import React, { useMemo, useState } from 'react';
import { useStore, put, remove, generateId } from '../hooks/useDB';
import PageLoader from '../components/PageLoader';
import { toast } from '../utils/toast';
import { confirm } from '../utils/confirm';
import {
  MessageSquare, MessageCircle, Bug, Lightbulb, Plus, X, Send,
  Trash2, Inbox, CheckCircle2, Clock, AlertCircle, ShieldCheck, Loader2,
} from 'lucide-react';

// ── Reference data ────────────────────────────────────────────────────────────
const TYPES = [
  { id: 'feedback',   label: 'Feedback',   icon: MessageCircle, hint: 'Share your thoughts about the offsite or this app',  color: '#4f46e5' },
  { id: 'issue',      label: 'Report Issue', icon: Bug,          hint: 'Something is broken or not working as expected',      color: '#dc2626' },
  { id: 'suggestion', label: 'Suggestion', icon: Lightbulb,      hint: 'An idea to make things better',                       color: '#7c3aed' },
];
const TYPE_MAP = TYPES.reduce((m, t) => ({ ...m, [t.id]: t }), {});

const PRIORITIES = ['low', 'medium', 'high'];
const STATUSES = ['open', 'in-progress', 'resolved'];

const STATUS_META = {
  'open':        { label: 'Open',        cls: 'bg-amber-50 text-amber-700 border-amber-200',     icon: AlertCircle },
  'in-progress': { label: 'In Progress', cls: 'bg-blue-50 text-blue-700 border-blue-200',        icon: Clock },
  'resolved':    { label: 'Resolved',    cls: 'bg-emerald-50 text-emerald-700 border-emerald-200', icon: CheckCircle2 },
};
const PRIORITY_CLS = {
  low:    'bg-slate-100 text-slate-600',
  medium: 'bg-amber-100 text-amber-700',
  high:   'bg-red-100 text-red-700',
};

function fmtDate(iso) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('en-IN', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });
  } catch { return iso; }
}

function TypeBadge({ type }) {
  const meta = TYPE_MAP[type] || TYPE_MAP.feedback;
  const Icon = meta.icon;
  return (
    <span
      className="inline-flex items-center gap-1.5 text-xs font-semibold px-2 py-1 rounded-lg"
      style={{ color: meta.color, background: `${meta.color}14` }}
    >
      <Icon size={12} className="shrink-0" />
      {meta.label}
    </span>
  );
}

function StatusBadge({ status }) {
  const meta = STATUS_META[status] || STATUS_META.open;
  const Icon = meta.icon;
  return (
    <span className={`inline-flex items-center gap-1.5 text-xs font-semibold px-2 py-1 rounded-lg border ${meta.cls}`}>
      <Icon size={12} className="shrink-0" />
      {meta.label}
    </span>
  );
}

// ── Submit modal ──────────────────────────────────────────────────────────────
function SubmitModal({ onSave, onClose }) {
  const [form, setForm] = useState({ type: 'feedback', title: '', message: '', priority: 'medium' });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const valid = form.title.trim() && form.message.trim();

  const submit = async () => {
    if (!valid) return;
    setSaving(true);
    try {
      await onSave(form);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="tw-drawer-backdrop">
      <div className="tw-drawer-panel">
        <div className="p-6 border-b border-slate-100 flex items-center justify-between sticky top-0 bg-white z-10">
          <div>
            <h3 className="text-lg font-semibold text-slate-900">Share Feedback</h3>
            <p className="text-xs text-slate-500 mt-1">Your message goes straight to the organizing team</p>
          </div>
          <button className="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-50 rounded-lg transition" onClick={onClose}>
            <X size={16} />
          </button>
        </div>

        <div className="p-6 space-y-5 overflow-y-auto flex-1">
          {/* Type selector */}
          <div className="space-y-2">
            <label className="text-xs font-semibold text-slate-600 uppercase tracking-wider">What would you like to share?</label>
            <div className="grid grid-cols-3 gap-2">
              {TYPES.map((t) => {
                const Icon = t.icon;
                const active = form.type === t.id;
                return (
                  <button
                    key={t.id}
                    type="button"
                    onClick={() => set('type', t.id)}
                    className={`flex flex-col items-center gap-1.5 p-3 rounded-xl border text-xs font-semibold transition ${
                      active ? 'border-transparent text-white shadow-sm' : 'border-slate-200 text-slate-600 hover:border-slate-300 bg-white'
                    }`}
                    style={active ? { background: t.color } : {}}
                  >
                    <Icon size={18} />
                    {t.label}
                  </button>
                );
              })}
            </div>
            <p className="text-[11px] text-slate-400">{TYPE_MAP[form.type].hint}</p>
          </div>

          {/* Priority — issues only */}
          {form.type === 'issue' && (
            <div className="space-y-1 animate-fadeIn">
              <label className="text-xs font-semibold text-slate-600 uppercase tracking-wider">Priority</label>
              <div className="flex gap-2">
                {PRIORITIES.map((p) => (
                  <button
                    key={p}
                    type="button"
                    onClick={() => set('priority', p)}
                    className={`flex-1 py-2 rounded-lg text-xs font-bold capitalize transition border ${
                      form.priority === p ? `${PRIORITY_CLS[p]} border-transparent` : 'bg-white border-slate-200 text-slate-500 hover:border-slate-300'
                    }`}
                  >
                    {p}
                  </button>
                ))}
              </div>
            </div>
          )}

          <div className="space-y-1">
            <label className="text-xs font-semibold text-slate-600 uppercase tracking-wider">Subject *</label>
            <input
              value={form.title}
              onChange={(e) => set('title', e.target.value)}
              placeholder={form.type === 'issue' ? 'e.g. RSVP button not saving' : 'e.g. Loved the itinerary layout'}
              className="w-full bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 text-sm focus:bg-white focus:border-slate-800 outline-none transition"
            />
          </div>

          <div className="space-y-1">
            <label className="text-xs font-semibold text-slate-600 uppercase tracking-wider">Details *</label>
            <textarea
              value={form.message}
              onChange={(e) => set('message', e.target.value)}
              rows={5}
              placeholder="Tell us more — steps to reproduce, what you expected, or your idea…"
              className="w-full bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 text-sm focus:bg-white focus:border-slate-800 outline-none transition resize-none"
            />
          </div>
        </div>

        <div className="p-6 border-t border-slate-100 flex gap-3 justify-end bg-white">
          <button className="px-4 py-2 text-sm font-semibold border border-slate-200 hover:bg-slate-50 rounded-lg transition" onClick={onClose}>
            Cancel
          </button>
          <button
            className="px-4 py-2 text-sm font-semibold bg-slate-900 hover:bg-slate-800 text-white rounded-lg transition disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center gap-2"
            onClick={submit}
            disabled={!valid || saving}
          >
            {saving ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
            {saving ? 'Sending…' : 'Submit'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Page ──────────────────────────────────────────────────────────────────────
export default function Feedback({ currentUser = {} }) {
  const isAdmin = currentUser?.role === 'admin';
  const { data: feedback, loading } = useStore('feedback');
  const [modalOpen, setModalOpen] = useState(false);
  const [statusFilter, setStatusFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');
  const [drafts, setDrafts] = useState({}); // id -> response text
  const [sendingIds, setSendingIds] = useState(new Set()); // ids with in-flight sendResponse
  const [statusIds, setStatusIds] = useState(new Set()); // ids with in-flight setStatus

  const setSending = (id, busy) => setSendingIds((prev) => { const n = new Set(prev); busy ? n.add(id) : n.delete(id); return n; });
  const setStatusBusy = (id, busy) => setStatusIds((prev) => { const n = new Set(prev); busy ? n.add(id) : n.delete(id); return n; });

  const myEmail = (currentUser.email || '').toLowerCase();

  // Newest first
  const sorted = useMemo(
    () => [...feedback].sort((a, b) => new Date(b.submittedAt || 0) - new Date(a.submittedAt || 0)),
    [feedback]
  );

  const mine = useMemo(
    () => sorted.filter((r) => (r.submittedByEmail || '').toLowerCase() === myEmail),
    [sorted, myEmail]
  );

  const adminVisible = useMemo(() => {
    return sorted.filter((r) => {
      if (statusFilter !== 'all' && r.status !== statusFilter) return false;
      if (typeFilter !== 'all' && r.type !== typeFilter) return false;
      return true;
    });
  }, [sorted, statusFilter, typeFilter]);

  const stats = useMemo(() => ({
    total: sorted.length,
    open: sorted.filter((r) => r.status === 'open').length,
    inProgress: sorted.filter((r) => r.status === 'in-progress').length,
    resolved: sorted.filter((r) => r.status === 'resolved').length,
  }), [sorted]);

  // ── Actions ──────────────────────────────────────────────────────────────
  const submit = async (form) => {
    const record = {
      id: generateId(),
      type: form.type,
      title: form.title.trim(),
      message: form.message.trim(),
      priority: form.type === 'issue' ? form.priority : null,
      status: 'open',
      submittedByName: currentUser.name || '',
      submittedByEmail: currentUser.email || '',
      submittedAt: new Date().toISOString(),
      adminResponse: '',
      respondedAt: null,
    };
    try {
      await put('feedback', record);
      setModalOpen(false);
      window.dispatchEvent(new CustomEvent('planner:data-change'));
      toast.success('Thanks! Your feedback reached the team.');
    } catch {
      toast.error('Could not submit. Please try again.');
    }
  };

  const setStatus = async (row, status) => {
    if (statusIds.has(row.id)) return;
    setStatusBusy(row.id, true);
    try {
      await put('feedback', { ...row, status });
      window.dispatchEvent(new CustomEvent('planner:data-change'));
      toast.success(`Marked as ${STATUS_META[status].label.toLowerCase()}.`);
    } catch {
      toast.error('Could not update status.');
    } finally {
      setStatusBusy(row.id, false);
    }
  };

  const sendResponse = async (row) => {
    const text = (drafts[row.id] ?? row.adminResponse ?? '').trim();
    if (!text) { toast.error('Write a response first.'); return; }
    if (sendingIds.has(row.id)) return;
    setSending(row.id, true);
    try {
      await put('feedback', {
        ...row,
        adminResponse: text,
        respondedAt: new Date().toISOString(),
        status: row.status === 'open' ? 'in-progress' : row.status,
      });
      setDrafts((d) => { const next = { ...d }; delete next[row.id]; return next; });
      window.dispatchEvent(new CustomEvent('planner:data-change'));
      toast.success('Response sent.');
    } catch {
      toast.error('Could not send response.');
    } finally {
      setSending(row.id, false);
    }
  };

  const del = async (row) => {
    const ok = await confirm(
      isAdmin
        ? `Delete "${row.title}" from ${row.submittedByName || 'the submitter'}? This cannot be undone.`
        : `Withdraw your feedback "${row.title}"? This cannot be undone.`,
      'Delete Feedback'
    );
    if (!ok) return;
    try {
      await remove('feedback', row.id);
      window.dispatchEvent(new CustomEvent('planner:data-change'));
      toast.success(isAdmin ? 'Feedback deleted.' : 'Feedback withdrawn.');
    } catch {
      toast.error('Could not delete.');
    }
  };

  if (loading) return <PageLoader label="Loading feedback…" sublabel="Fetching the latest reports" />;

  // ── Employee view ────────────────────────────────────────────────────────
  if (!isAdmin) {
    return (
      <div className="space-y-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h2 className="text-2xl font-bold font-display text-slate-900 flex items-center gap-2">
              <MessageSquare style={{ color: 'var(--accent)' }} size={24} />
              <span>Feedback &amp; Support</span>
            </h2>
            <p className="text-sm text-slate-500 mt-1">
              Report an issue, share feedback, or suggest an idea — the organizing team reads every one.
            </p>
          </div>
          <button
            className="px-4 py-2 bg-slate-900 hover:bg-slate-800 text-white rounded-lg text-sm font-semibold transition flex items-center gap-2 self-start sm:self-auto shadow-sm"
            onClick={() => setModalOpen(true)}
          >
            <Plus size={16} />
            <span>New Feedback</span>
          </button>
        </div>

        {mine.length === 0 ? (
          <div className="bg-white border border-slate-200 rounded-xl p-16 text-center shadow-sm">
            <MessageSquare className="mx-auto text-slate-300 mb-3" size={36} />
            <h3 className="text-base font-bold text-slate-800 font-display">No feedback yet</h3>
            <p className="text-sm text-slate-500 mt-1 max-w-sm mx-auto">
              Have a question, hit a bug, or got an idea for the offsite? Tap <span className="font-semibold">New Feedback</span> and let us know.
            </p>
            <button className="mt-5 px-4 py-2 bg-slate-900 hover:bg-slate-800 text-white rounded-lg text-sm font-semibold transition inline-flex items-center gap-2" onClick={() => setModalOpen(true)}>
              <Plus size={16} /> Share something
            </button>
          </div>
        ) : (
          <div className="space-y-3">
            {mine.map((row) => (
              <div key={row.id} className="bg-white border border-slate-200 rounded-xl p-4 shadow-sm">
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div className="flex items-center gap-2 flex-wrap">
                    <TypeBadge type={row.type} />
                    {row.priority && <span className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded ${PRIORITY_CLS[row.priority]}`}>{row.priority}</span>}
                    <StatusBadge status={row.status} />
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-slate-400 font-mono">{fmtDate(row.submittedAt)}</span>
                    {row.status !== 'resolved' && (
                      <button onClick={() => del(row)} title="Withdraw" className="text-slate-400 hover:text-red-600 hover:bg-red-50 p-1 rounded transition">
                        <Trash2 size={14} />
                      </button>
                    )}
                  </div>
                </div>
                <div className="mt-2.5 font-semibold text-slate-900">{row.title}</div>
                <p className="text-sm text-slate-600 mt-1 whitespace-pre-wrap leading-relaxed">{row.message}</p>

                {row.adminResponse ? (
                  <div className="mt-3 bg-orange-50/60 border border-orange-100 rounded-lg p-3">
                    <div className="flex items-center gap-1.5 text-xs font-bold text-orange-700 mb-1">
                      <ShieldCheck size={13} /> Team response
                      {row.respondedAt && <span className="text-orange-400 font-normal ml-1">· {fmtDate(row.respondedAt)}</span>}
                    </div>
                    <p className="text-sm text-slate-700 whitespace-pre-wrap leading-relaxed">{row.adminResponse}</p>
                  </div>
                ) : (
                  <div className="mt-3 text-xs text-slate-400 italic">Awaiting a response from the team…</div>
                )}
              </div>
            ))}
          </div>
        )}

        {modalOpen && <SubmitModal onSave={submit} onClose={() => setModalOpen(false)} />}
      </div>
    );
  }

  // ── Admin view ───────────────────────────────────────────────────────────
  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold font-display text-slate-900 flex items-center gap-2">
            <Inbox style={{ color: 'var(--accent)' }} size={24} />
            <span>Feedback Inbox</span>
          </h2>
          <p className="text-sm text-slate-500 mt-1">
            Triage issues, respond to employees, and track everything to resolution.
          </p>
        </div>
        <button
          className="px-4 py-2 border border-slate-200 hover:bg-slate-50 text-slate-700 rounded-lg text-sm font-semibold transition flex items-center gap-2 self-start sm:self-auto"
          onClick={() => setModalOpen(true)}
        >
          <Plus size={16} />
          <span>Add Note</span>
        </button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        <div className="sh-stat-card border-l-4 border-slate-900">
          <div className="sh-stat-head"><span className="sh-stat-label">Total</span><MessageSquare className="text-slate-500" size={14} /></div>
          <div className="sh-stat-value text-slate-900 font-display">{stats.total}</div>
        </div>
        <div className="sh-stat-card border-l-4 border-amber-500">
          <div className="sh-stat-head"><span className="sh-stat-label">Open</span><AlertCircle className="text-amber-500" size={14} /></div>
          <div className="sh-stat-value text-slate-900 font-display">{stats.open}</div>
        </div>
        <div className="sh-stat-card border-l-4 border-blue-500">
          <div className="sh-stat-head"><span className="sh-stat-label">In Progress</span><Clock className="text-blue-500" size={14} /></div>
          <div className="sh-stat-value text-slate-900 font-display">{stats.inProgress}</div>
        </div>
        <div className="sh-stat-card border-l-4 border-emerald-500">
          <div className="sh-stat-head"><span className="sh-stat-label">Resolved</span><CheckCircle2 className="text-emerald-500" size={14} /></div>
          <div className="sh-stat-value text-emerald-600 font-display">{stats.resolved}</div>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-2">
        <div className="flex items-center gap-1 bg-slate-100 rounded-lg p-1">
          {['all', ...STATUSES].map((s) => (
            <button
              key={s}
              onClick={() => setStatusFilter(s)}
              className={`px-3 py-1.5 rounded-md text-xs font-semibold capitalize transition ${
                statusFilter === s ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'
              }`}
            >
              {s === 'all' ? 'All' : STATUS_META[s].label}
            </button>
          ))}
        </div>
        <div className="flex items-center gap-1 bg-slate-100 rounded-lg p-1">
          {['all', ...TYPES.map((t) => t.id)].map((t) => (
            <button
              key={t}
              onClick={() => setTypeFilter(t)}
              className={`px-3 py-1.5 rounded-md text-xs font-semibold capitalize transition ${
                typeFilter === t ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'
              }`}
            >
              {t === 'all' ? 'All types' : TYPE_MAP[t].label}
            </button>
          ))}
        </div>
      </div>

      {/* List */}
      {adminVisible.length === 0 ? (
        <div className="bg-white border border-slate-200 rounded-xl p-16 text-center shadow-sm">
          <Inbox className="mx-auto text-slate-300 mb-3" size={36} />
          <h3 className="text-base font-bold text-slate-800 font-display">Nothing here</h3>
          <p className="text-sm text-slate-500 mt-1">
            {stats.total === 0 ? 'No feedback has been submitted yet.' : 'No items match the current filters.'}
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {adminVisible.map((row) => (
            <div key={row.id} className="bg-white border border-slate-200 rounded-xl p-4 shadow-sm">
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div className="flex items-center gap-2 flex-wrap">
                  <TypeBadge type={row.type} />
                  {row.priority && <span className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded ${PRIORITY_CLS[row.priority]}`}>{row.priority}</span>}
                  <StatusBadge status={row.status} />
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-xs text-slate-400 font-mono">{fmtDate(row.submittedAt)}</span>
                  <button onClick={() => del(row)} title="Delete" className="text-slate-400 hover:text-red-600 hover:bg-red-50 p-1 rounded transition">
                    <Trash2 size={14} />
                  </button>
                </div>
              </div>

              <div className="mt-2.5 font-semibold text-slate-900">{row.title}</div>
              <p className="text-sm text-slate-600 mt-1 whitespace-pre-wrap leading-relaxed">{row.message}</p>

              <div className="mt-2 text-xs text-slate-500">
                From <span className="font-semibold text-slate-700">{row.submittedByName || 'Unknown'}</span>
                {row.submittedByEmail && <span className="text-slate-400"> · {row.submittedByEmail}</span>}
              </div>

              {/* Triage controls */}
              <div className="mt-3 pt-3 border-t border-slate-100 flex flex-col gap-3">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-xs font-semibold text-slate-500">Status:</span>
                  {STATUSES.map((s) => (
                    <button
                      key={s}
                      onClick={() => setStatus(row, s)}
                      disabled={statusIds.has(row.id)}
                      className={`px-2.5 py-1 rounded-lg text-xs font-semibold border transition disabled:opacity-60 disabled:cursor-not-allowed inline-flex items-center gap-1 ${
                        row.status === s ? STATUS_META[s].cls : 'bg-white border-slate-200 text-slate-500 hover:border-slate-300'
                      }`}
                    >
                      {statusIds.has(row.id) && row.status !== s && <Loader2 size={10} className="animate-spin" />}
                      {STATUS_META[s].label}
                    </button>
                  ))}
                </div>

                <div className="flex items-end gap-2">
                  <div className="flex-1">
                    <textarea
                      value={drafts[row.id] ?? row.adminResponse ?? ''}
                      onChange={(e) => setDrafts((d) => ({ ...d, [row.id]: e.target.value }))}
                      rows={2}
                      placeholder="Write a response to the employee…"
                      className="w-full bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 text-sm focus:bg-white focus:border-slate-800 outline-none transition resize-none"
                    />
                  </div>
                  <button
                    onClick={() => sendResponse(row)}
                    disabled={sendingIds.has(row.id)}
                    className="px-3 py-2 bg-slate-900 hover:bg-slate-800 text-white rounded-lg text-xs font-semibold transition inline-flex items-center gap-1.5 shrink-0 disabled:opacity-60 disabled:cursor-not-allowed"
                  >
                    {sendingIds.has(row.id) ? <Loader2 size={13} className="animate-spin" /> : <Send size={13} />}
                    {sendingIds.has(row.id) ? 'Sending…' : 'Send'}
                  </button>
                </div>
                {row.adminResponse && row.respondedAt && (
                  <div className="text-[11px] text-slate-400">Last responded {fmtDate(row.respondedAt)}</div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {modalOpen && <SubmitModal onSave={submit} onClose={() => setModalOpen(false)} />}
    </div>
  );
}
